import apiInstance from "./index";

/**
 * Tiny RFC-4122 v4 UUID generator — used for Idempotency-Key and
 * X-Correlation-Id headers. Doesn't need crypto quality; we just need a
 * high-uniqueness string for de-duplication.
 */
const uuidv4 = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const mutationHeaders = (explicitCorrelationId) => ({
  "Idempotency-Key": uuidv4(),
  "X-Correlation-Id": explicitCorrelationId || uuidv4(),
});

export const PortalAssetsApiService = {
  /**
   * GET /api/portal/assets
   * @param {{locationId?:number, routeId?:number, escalationOnly?:boolean,
   *          assetType?:string, serviceStatus?:string, workflowStage?:string}} filter
   */
  listAssets: (filter = {}) => {
    return apiInstance.get("/portal/assets", { params: filter });
  },

  getAsset: (assetRef) => {
    return apiInstance.get(`/portal/assets/${encodeURIComponent(assetRef)}`);
  },

  transition: (assetRef, body, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/transition`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  changeServiceStatus: (assetRef, body, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/service-status`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  createEscalation: (assetRef, body, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/escalations`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  acknowledgeEscalation: (assetRef, escalationId, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/escalations/${escalationId}/acknowledge`,
      {},
      { headers: mutationHeaders(correlationId) }
    );
  },

  resolveEscalation: (assetRef, escalationId, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/escalations/${escalationId}/resolve`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  confirmEmpty: (assetRef, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/confirm-empty`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  verifyEmpty: (assetRef, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/verify`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  submitApproval: (assetRef, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/approval`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  recordPayment: (assetRef, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/payment`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  closeFinancially: (assetRef, body = {}, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/financial-close`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  linkSensor: (assetRef, body, correlationId) => {
    return apiInstance.post(
      `/portal/assets/${encodeURIComponent(assetRef)}/sensor-link`,
      body,
      { headers: mutationHeaders(correlationId) }
    );
  },

  unlinkSensor: (assetRef, sensorId, correlationId) => {
    return apiInstance.delete(
      `/portal/assets/${encodeURIComponent(assetRef)}/sensor-link/${sensorId}`,
      { headers: mutationHeaders(correlationId) }
    );
  },

  /**
   * GET /api/portal/my-capabilities
   * Resolves the caller's portal role + permitted actions so the SPA can
   * gate the vendor-portal sidebar against the same permissions the API
   * enforces. Response data: { portalRole, allowedActions[], isPlatformAdmin }.
   */
  getMyCapabilities: () => {
    return apiInstance.get("/portal/my-capabilities");
  },

  getMorningBrief: () => {
    return apiInstance.get("/portal/morning-brief");
  },

  getRouteReport: (routeId) => {
    return apiInstance.get(`/portal/routes/${routeId}/report`);
  },

  getTimeline: (assetRef, limit = 50) => {
    return apiInstance.get("/portal/timeline", {
      params: { assetRef, limit },
    });
  },
};

export default PortalAssetsApiService;
